import { useCallback, type ReactNode } from "react";
import type { AccountPatch, RegisteredAccount } from "../../types/account";
import { accountPurposeLabel, accountTypeLabel } from "../../data/accountTypes";
import { formatAmount } from "../../utils/currency";
import { formatLongDate } from "../../utils/dates";
import { accountName, questionCard } from "./accountFacts";
import { AccountName } from "./AccountName";
import { IdentifyQuestion } from "./IdentifyQuestion";
import { OpeningQuestion } from "./OpeningQuestion";
import { PersonalMove } from "./PersonalMove";
import { PostedRowsList } from "./PostedRowsList";
import { ReplacementQuestion } from "./ReplacementQuestion";

interface AccountRowProps {
  clientId: number;
  account: RegisteredAccount;
  /** True when another row would read the same: the routing number's end is shown. */
  showRouting: boolean;
  onAnswer: (rowId: number, body: AccountPatch) => Promise<void>;
  onChanged: () => void;
}

/** The starting-balance state: the as-entered figure on its date, or "not entered". */
function openingState(account: RegisteredAccount): string {
  if (!account.opening_established || account.opening_as_of === null) return "not entered";
  const figure = formatAmount(account.opening_balance_as_entered, account.currency);
  return `${figure} on ${formatLongDate(account.opening_as_of)}`;
}

/** A fact's span, or nothing when the fact is empty. */
function fact(className: string, text: string | null): ReactNode {
  return text ? (
    <span key={className} className={className}>
      {text}
    </span>
  ) : null;
}

/** The facts with one space between each, skipping the empty ones (finding 1). */
function spaced(parts: ReactNode[]): ReactNode[] {
  return parts.filter(Boolean).flatMap((part, i) => (i === 0 ? [part] : [" ", part]));
}

/**
 * The row's facts (walk-through round 1, findings 1 and 2): the nickname
 * first when there is one, then the institution, the type's label and the
 * number ("ending 0011"); without a nickname the number is the name the
 * client presses to give one. Then the purpose's label and the starting
 * balance's state. When another row would read the same, the served
 * `routing_last4` follows the number as "routing ending 0015".
 */
function AccountFacts({
  account,
  showRouting,
  answer,
}: {
  account: RegisteredAccount;
  showRouting: boolean;
  answer: (body: AccountPatch) => Promise<void>;
}): ReactNode {
  const name = (
    <AccountName
      key="name"
      name={accountName(account)}
      nickname={account.nickname}
      onAnswer={answer}
    />
  );
  const ending = account.last6 ? `ending ${account.last6}` : null;
  const parts = [
    account.nickname ? name : null,
    fact("account-institution", account.institution_name),
    fact("account-type", accountTypeLabel(account.account_type)),
    account.nickname ? fact("account-ending", ending) : name,
    showRouting && account.routing_last4
      ? fact("account-routing", `routing ending ${account.routing_last4}`)
      : null,
    fact("account-purpose", accountPurposeLabel(account.purpose)),
    fact("account-opening", openingState(account)),
  ];
  return <div className="account-row-facts">{spaced(parts)}</div>;
}

function QuestionCard({
  account,
  answer,
}: {
  account: RegisteredAccount;
  answer: (body: AccountPatch) => Promise<void>;
}): ReactNode {
  switch (questionCard(account)) {
    case "replacement":
      return (
        <ReplacementQuestion
          question={account.replacement_candidate?.question ?? ""}
          onAnswer={answer}
        />
      );
    case "opening":
      return <OpeningQuestion account={account} onAnswer={answer} />;
    case "identify":
      return <IdentifyQuestion onAnswer={answer} />;
    default:
      return null;
  }
}

/**
 * One registered account: its facts, its one question card (accountFacts
 * `questionCard`), the server's notices under it, the personal move, and
 * the move's list of rows already in the business books.
 */
export function AccountRow({
  clientId,
  account,
  showRouting,
  onAnswer,
  onChanged,
}: AccountRowProps): ReactNode {
  const answer = useCallback(
    (body: AccountPatch) => onAnswer(account.id, body),
    [onAnswer, account],
  );
  const steer = account.pending_questions.includes("opening") && account.earliest_statement_start;

  return (
    <li className="account-row" data-account-id={account.id}>
      <AccountFacts account={account} showRouting={showRouting} answer={answer} />
      {steer && (
        <p className="account-steer">
          {`Your earliest statement starts on ${formatLongDate(account.earliest_statement_start)}; ` +
            "upload any older statement first."}
        </p>
      )}
      <QuestionCard account={account} answer={answer} />
      {account.notices.map((notice) => (
        <p className="account-notice" key={notice}>
          {notice}
        </p>
      ))}
      {account.purpose !== "personal" && <PersonalMove onAnswer={answer} />}
      <PostedRowsList
        clientId={clientId}
        rows={account.posted_rows}
        currency={account.currency}
        onChanged={onChanged}
      />
    </li>
  );
}
